import { t } from "@travily/i18n";
import { Dialog } from "@ui/components/Dialog";
import { createContext, type ReactNode, useContext, useMemo, useState } from "react";

interface ExploreFiltersContextValue {
  openFilters: () => void;
}

const ExploreFiltersContext = createContext<ExploreFiltersContextValue | null>(null);

/**
 * Read by the search-centric top bar to decide whether to show a filters button and how to open it.
 * Returns `null` on sections without a filters modal (the Explore home) — the top bar then
 * renders no filters button, satisfying "if no filters modal, no button".
 */
export function useExploreFilters(): ExploreFiltersContextValue | null {
  return useContext(ExploreFiltersContext);
}

export interface ExploreFiltersProviderProps {
  /** Page tree (includes `PageFrame`, whose top-bar filters button consumes this context). */
  children: ReactNode;
  /** Filters body rendered inside the modal. Lives within the page's `<InstantSearch>` context (the
   *  provider sits inside it) so its refinement hooks / filter state stay connected. */
  filters: ReactNode;
  /** Sticky modal footer; receives a `close` callback. */
  footer?: (close: () => void) => ReactNode;
  size?: React.ComponentProps<typeof Dialog.Root>["size"];
}

/**
 * Bridges the page-local filters modal to the global search-centric top bar. The section page wraps
 * `PageFrame` with this provider (inside its `<InstantSearch>`), passing the filters body as
 * `filters`; the top bar's filters button calls `openFilters()`. Only mounted on sections that
 * have filters (Destinations, Programs, Marketplace).
 */
export function ExploreFiltersProvider({ children, filters, footer, size = "sm" }: ExploreFiltersProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const value = useMemo(() => ({ openFilters: () => setIsOpen(true) }), []);
  const close = () => setIsOpen(false);

  return (
    <ExploreFiltersContext.Provider value={value}>
      {children}
      <Dialog.Root isOpen={isOpen} onOpenChange={setIsOpen} size={size}>
        <Dialog.Content>
          <Dialog.Header onClose={close} title={t("components.search_bar.filters_title")} />
          <Dialog.Body>{filters}</Dialog.Body>
          {footer ? <Dialog.Footer>{footer(close)}</Dialog.Footer> : null}
        </Dialog.Content>
      </Dialog.Root>
    </ExploreFiltersContext.Provider>
  );
}
